import { createRoute, lazyRouteComponent } from '@tanstack/react-router';
import { z } from 'zod/v3';

import { AutomationStatus } from 'common/api-types/ordering-automation';
import { BudgetThreshold } from 'common/api-types/vendor';
import { menuLayoutRoute } from 'root/MenuLayout';
import RequireSingleCountry from 'root/RequireSingleCountry';

import {
  FORECAST_OVERVIEW_CHART_LINES,
  FORECAST_OVERVIEW_TIME_SERIES_LINES,
  OVERVIEW_RANGE,
} from './forecast/constants';
import { ForecastOverviewChartDataKey, OverviewRange } from './forecast/types';
import { AUTOMATION_STATUSES } from './ordering-automation/constants';

const inventoryReplenishmentRoute = createRoute({
  getParentRoute: () => menuLayoutRoute,
  id: 'inventory-replenishment',
  component: RequireSingleCountry,
});

const eventsRoute = createRoute({
  getParentRoute: () => inventoryReplenishmentRoute,
  path: 'events',
  component: lazyRouteComponent(() => import('./events/EventsView/EventsView')),
  validateSearch: z.object({
    date: z.string().optional(),
    createOccasion: z.boolean().optional(),
    createVenueClosure: z.boolean().optional(),
    editOccasionId: z.string().optional(),
    editVenueClosureId: z.string().optional(),
    venueIds: z.array(z.string()).optional(),
    showOccasions: z.boolean().optional().default(true),
    showVenueClosures: z.boolean().optional().default(true),
    showPromotions: z.boolean().optional().default(true),
    uploadPromotions: z.boolean().optional(),
    deletePromotions: z.boolean().optional(),
  }),
});

const forecastRoute = createRoute({
  getParentRoute: () => inventoryReplenishmentRoute,
  path: 'forecast',
  component: lazyRouteComponent(() => import('./forecast/ForecastView/ForecastView')),
  validateSearch: z.object({
    chartLines: z
      .array(
        z.custom<ForecastOverviewChartDataKey>((data) =>
          Object.keys(FORECAST_OVERVIEW_CHART_LINES)
            .concat(Object.keys(FORECAST_OVERVIEW_TIME_SERIES_LINES))
            .includes(data),
        ),
      )
      .optional(),
    deepDiveModalProductId: z.string().optional(),
    deepDiveModalVenueId: z.string().optional(),
    productId: z.string().optional(),
    range: z.custom<OverviewRange>((data) => OVERVIEW_RANGE.includes(data)).optional(),
    venueIds: z.array(z.string()).optional(),
    productFamilyIds: z.array(z.string()).optional(),
    overridesPresence: z.enum(['WITH_OVERRIDES', 'WITHOUT_OVERRIDES']).optional(),
    view: z.string().optional(),
    askAi: z.boolean().optional(),
  }),
});

const forecastEventRoute = createRoute({
  getParentRoute: () => inventoryReplenishmentRoute,
  path: 'forecast/$eventId',
  component: lazyRouteComponent(() => import('./forecast/EventView/EventView')),
  validateSearch: z.object({
    from: z.string().optional(),
    productId: z.string().optional(),
    view: z.string().optional(),
  }),
});

const forecastSetupRoute = createRoute({
  getParentRoute: () => inventoryReplenishmentRoute,
  path: 'forecast-setup',
  component: lazyRouteComponent(
    () => import('./forecast-setup/ForecastSetupView/ForecastSetupView'),
  ),
  validateSearch: z.object({
    formulaId: z.string().optional(),
    page: z.coerce.number().optional(),
    pageSize: z.coerce.number().optional(),
    productFamilyIds: z.array(z.string()).optional(),
    productSegments: z.array(z.string()).optional(),
    q: z.coerce.string().optional(),
    venueIds: z.array(z.string()).optional(),
  }),
});

const safetyStockSetupRoute = createRoute({
  getParentRoute: () => inventoryReplenishmentRoute,
  path: 'safety-stock-setup',
  component: lazyRouteComponent(
    () => import('./safety-stock-setup/SafetyStockSetupView/SafetyStockSetupView'),
  ),
  validateSearch: z.object({
    settingId: z.string().optional(),
    page: z.coerce.number().optional(),
    pageSize: z.coerce.number().optional(),
    productFamilyIds: z.array(z.string()).optional(),
    productSegments: z.array(z.string()).optional(),
    q: z.coerce.string().optional(),
    warehouseIds: z.array(z.string()).optional(),
  }),
});

const productRelationshipRoute = createRoute({
  getParentRoute: () => inventoryReplenishmentRoute,
  path: 'product-relationship',
  component: lazyRouteComponent(
    () => import('./product-relationship/ProductRelationshipView/ProductRelationshipView'),
  ),
  validateSearch: z.object({
    poolId: z.string().optional(),
    page: z.coerce.number().optional(),
    pageSize: z.coerce.number().optional(),
    q: z.coerce.string().optional(),
  }),
});

const orderingAutomationSetupRoute = createRoute({
  getParentRoute: () => inventoryReplenishmentRoute,
  path: 'ordering-automation',
  component: lazyRouteComponent(
    () => import('./ordering-automation/VendorsListView/VendorsListView'),
  ),
  validateSearch: z.object({
    page: z.coerce.number().optional(),
    pageSize: z.coerce.number().optional(),
    q: z.coerce.string().optional(),
    warehouseIds: z.array(z.string()).optional(),
    automationStatuses: z
      .array(
        z.custom<AutomationStatus>((data) =>
          AUTOMATION_STATUSES.includes(data as AutomationStatus),
        ),
      )
      .optional(),
  }),
});

const orderingAutomationSetupVendorRoute = createRoute({
  getParentRoute: () => inventoryReplenishmentRoute,
  path: 'ordering-automation/$vendorId',
  params: z.object({ vendorId: z.string() }),
  component: lazyRouteComponent(
    () => import('./ordering-automation/AutomationSetupView/AutomationSetupView'),
  ),
  validateSearch: z.object({
    configId: z.string().optional(),
    q: z.string().optional(),
  }),
});

const weeklyTrendsRoute = createRoute({
  getParentRoute: () => inventoryReplenishmentRoute,
  path: 'weekly-trends',
  component: lazyRouteComponent(() => import('./weekly-trends/WeeklyTrendsView/WeeklyTrendsView')),
  validateSearch: z.object({
    bulkUpdateModalOpen: z.boolean().optional(),
    q: z.coerce.string().optional(),
    weeklyTrendId: z.string().optional(),
  }),
});

const purchaseOrderBudgetsRoute = createRoute({
  getParentRoute: () => inventoryReplenishmentRoute,
  path: 'purchase-order-budgets',
  component: lazyRouteComponent(
    () => import('./purchase-order-budgets/PurchaseOrderBudgetsView/PurchaseOrderBudgetsView'),
  ),
  validateSearch: z.object({
    budgetId: z.string().optional(),
    createBudgetModalOpen: z.boolean().optional(),
    budgetHistoryModalOpen: z.boolean().optional(),
    q: z.coerce.string().optional(),
    vendorCodes: z.array(z.string()).optional(),
    utilizedAmount: z.custom<BudgetThreshold>().optional().nullable(),
    year: z.coerce.string().optional().nullable().default(new Date().getFullYear().toString()),
    page: z.coerce.number().optional(),
    pageSize: z.coerce.number().optional(),
  }),
});

export const inventoryReplenishmentRouteTree = inventoryReplenishmentRoute.addChildren({
  eventsRoute,
  forecastRoute,
  forecastEventRoute,
  forecastSetupRoute,
  safetyStockSetupRoute,
  orderingAutomationSetupRoute,
  orderingAutomationSetupVendorRoute,
  productRelationshipRoute,
  weeklyTrendsRoute,
  purchaseOrderBudgetsRoute,
});
